import React, { useState } from 'react';
import { Appbar } from 'react-native-paper';
import { useRouter, usePathname } from 'expo-router';
import styled from '@emotion/native';
import { green, accent } from '@/shared/styles/colors';
import { ROUTES } from '@/shared/constants/routes';
import { H1 } from '@/shared/components/StyledText';
import DropdownMenu from '@/shared/components/navigation/DropdownMenu';
import NotificationDropdown from '@/components/notifications/NotificationDropdown';
import { useInstantStylingToggle } from '@/shared/hooks/useInstantStylingToggle';
import { RouteType } from '@/shared/types/navigation';
import { isPathMatching } from '@/shared/utils';
import useInstantStylingStore from '@/stores/useInstantStylingStore';

const DesktopHeader = () => {
  const router = useRouter();
  const pathname = usePathname();

  const isInstantStylingActive = useInstantStylingStore(
    (state) => state.isInstantStylingActive,
  );
  const { toggleInstantStyling } = useInstantStylingToggle();

  const [isSubNavVisible, setIsSubNavVisible] = useState(false);
  const [isNotificationDropdownVisible, setIsNotificationDropdownVisible] =
    useState(false);

  const getPageTitle = () => {
    if (pathname.startsWith('/client-notes/')) {
      return 'Private Notes';
    }

    const matchingRoute = Object.values(ROUTES).find((route) =>
      isPathMatching(route.path, pathname),
    );
    return matchingRoute?.label ?? '';
  };

  const handleToggleInstantStyling = async () => {
    const newInstantStylingState = await toggleInstantStyling();

    if (!newInstantStylingState) return;

    router.navigate(ROUTES.INSTANT_STYLING.path);
  };

  const isRouteActive = (route: RouteType) => {
    return isPathMatching(route.path, pathname);
  };

  return (
    <HeaderContainer>
      <StyledAppBar>
        <TitleContainer>
          <PageTitle>{getPageTitle()}</PageTitle>
        </TitleContainer>

        <NavControls>
          <ToggleButton
            onPress={handleToggleInstantStyling}
            accessibilityLabel={
              isInstantStylingActive
                ? 'Disable Instant Styling'
                : 'Enable Instant Styling'
            }
          >
            <ToggleImage
              source={
                isInstantStylingActive
                  ? require('@/assets/images/InstantToggleOn.png')
                  : require('@/assets/images/InstantToggleOff.png')
              }
              resizeMode="contain"
              alt={`Instant styling ${isInstantStylingActive ? 'on' : 'off'}`}
            />
          </ToggleButton>

          <AppbarAction
            // TODO(154): Show chat alert icon when user has unread messages
            icon={require('@/assets/images/ChatIcon.png')}
            iconColor={green}
            rippleColor="transparent"
            size={24}
            onPress={() => router.push(ROUTES.MESSAGES.path)}
            accessibilityLabel="View Messages"
            backgroundColor={
              isRouteActive(ROUTES.MESSAGES) ? accent : 'transparent'
            }
          />

          <NotificationDropdown
            isVisible={isNotificationDropdownVisible}
            onDismiss={() => setIsNotificationDropdownVisible(false)}
            onPress={() =>
              setIsNotificationDropdownVisible(!isNotificationDropdownVisible)
            }
            renderAnchor={({ onPress, hasUnreadNotifications, isVisible }) => (
              <AppbarAction
                icon={
                  hasUnreadNotifications
                    ? require('@/assets/images/NotificationIconAlert.png')
                    : require('@/assets/images/NotificationIcon.png')
                }
                iconColor={green}
                rippleColor="transparent"
                size={24}
                onPress={onPress}
                accessibilityLabel="View Notifications"
                backgroundColor={isVisible ? accent : 'transparent'}
              />
            )}
          />

          <DropdownMenu
            isVisible={isSubNavVisible}
            onDismiss={() => setIsSubNavVisible(false)}
            onPress={() => setIsSubNavVisible(!isSubNavVisible)}
          />
        </NavControls>
      </StyledAppBar>
    </HeaderContainer>
  );
};

export default DesktopHeader;

const HeaderContainer = styled.View({
  width: '100%',
  backgroundColor: 'transparent',
});

const StyledAppBar = styled(Appbar)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 36,
  paddingBottom: 12,
  paddingHorizontal: 50,
  backgroundColor: 'transparent',
  flex: 1,
});

const AppbarAction = styled(Appbar.Action)<{ backgroundColor?: string }>(
  ({ backgroundColor = 'transparent' }) => ({
    margin: 0,
    borderRadius: 50,
    borderWidth: 0,
    backgroundColor: backgroundColor,
  }),
);

const TitleContainer = styled.View({
  flex: 1,
});

const PageTitle = styled(H1)({
  fontSize: 38,
  textTransform: 'uppercase',
  color: green,
});

const NavControls = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
});

const ToggleButton = styled.TouchableOpacity({
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: 10,
});

const ToggleImage = styled.Image({
  width: 192,
  height: 48,
});
